/**
 * IDI-308: the signing link, shown after a document is sent for signature.
 *
 * Shared by Quote and Change Order Review & Send. The quote used to toast and
 * navigate to /quotes, so the user never saw the link — and the link is the
 * e-sign fallback whenever an email doesn't arrive or there is no email on
 * file (P-23). Every outcome now lands here with the link and a Copy button.
 *
 * The copy is driven by `deliveryStatus` (the quote send's delivery_status):
 *   sent            "Sent to <sentTo>. If it doesn't arrive, copy the link."
 *   redirected      dev only: went to the test inbox, NOT the customer
 *   partial         some recipients failed
 *   no_recipient    no email on file
 *   not_configured  email isn't set up
 *   anything else   "the email was not sent", with the detail
 * IDI-92: never claim an email went out unless deliveryStatus === 'sent'.
 * IDI-464: change orders email too, so the old "no email yet" CO copy is gone.
 */
import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';

export function signingLinkFor(token) {
  return `${window.location.origin}/signature/${token}`;
}

/**
 * The absolute link to show. Prefers the server's link; falls back to one
 * built from the token (the server returns '' when APP_BASE_URL is unset).
 */
export function resolveSigningLink(serverLink, token) {
  if (typeof serverLink === 'string' && /^https?:\/\//.test(serverLink)) return serverLink;
  return token ? signingLinkFor(token) : '';
}

const NOUN = { quote: 'Quote', change_order: 'Change order' };

export function deliveryMessage({ deliveryStatus, deliveryDetail, sentTo, recipient }) {
  const who = sentTo || recipient;
  switch (deliveryStatus) {
    case 'sent':
      return `Sent to ${who || 'the customer'}. If it doesn't arrive, copy the link and send it yourself.`;
    case 'redirected':
      // Non-production: the dev mail guard sent it to the test inbox, so the
      // customer did NOT get it. Never say "Sent to <customer>" here.
      return `Test environment: this went to the test inbox${sentTo ? ` (${sentTo})` : ''}, not to the customer. Use the link below to test signing.`;
    case 'partial':
      return `Some recipients weren't reached${deliveryDetail ? ` (${deliveryDetail})` : ''}. Copy the link for anyone who missed it.`;
    case 'no_recipient':
      return 'No email is on file for this customer, so nothing was emailed. Copy the link and send it yourself.';
    case 'not_configured':
      return 'Email isn\u2019t set up yet, so nothing was emailed. Copy the link and send it yourself.';
    default:
      return `The email was not sent${deliveryDetail || deliveryStatus ? ` (${deliveryDetail || deliveryStatus})` : ''}. Copy the link and send it yourself.`;
  }
}

function formatExpiry(expiresAt) {
  if (!expiresAt) return '';
  const d = new Date(expiresAt);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * @param {object} props
 * @param {string} props.url              the signing link
 * @param {'quote'|'change_order'} [props.documentType]
 * @param {string} [props.deliveryStatus]
 * @param {string} [props.deliveryDetail]
 * @param {string} [props.sentTo]          who the server emailed
 * @param {string} [props.recipient]       who the user picked (when nothing was emailed)
 * @param {string} [props.expiresAt]
 * @param {string} props.doneLabel
 * @param {function} props.onDone
 */
export function SigningLinkResult({
  url, documentType = 'quote', deliveryStatus, deliveryDetail, sentTo, recipient,
  expiresAt, doneLabel, onDone,
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // The link stays selectable in the field below.
    }
  };
  const sent = deliveryStatus === 'sent';
  const noun = NOUN[documentType] || 'Document';
  const expiry = formatExpiry(expiresAt);
  return (
    <div className="p-6 max-w-2xl">
      <Card className="p-6 space-y-4">
        <h2 style={{ font: 'var(--text-h3)', color: 'var(--color-text-default)' }}>
          {sent ? `${noun} sent for signature` : `${noun} ready to sign`}
        </h2>
        <p className="text-sm" role="status" style={{ color: 'var(--color-text-secondary)' }}>
          {deliveryMessage({ deliveryStatus, deliveryDetail, sentTo, recipient })}
          {' '}It stays pending until they sign or decline.
        </p>
        {/* IDI-464 / IDI-308 (Sarah, 10-13 co-UAT L401: "What is this?"): the
            heading says plainly what the link is for, quote and CO alike. */}
        <label htmlFor="signing-link" className="block text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
          Signing link to share if the email doesn&apos;t arrive
        </label>
        <div className="flex gap-2">
          <input
            id="signing-link"
            readOnly
            value={url}
            onFocus={(e) => e.target.select()}
            className="input flex-1"
          />
          <Button variant="secondary" onClick={copy} disabled={!url}>{copied ? 'Copied' : 'Copy link'}</Button>
        </div>
        {expiry && (
          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Link expires {expiry}.</p>
        )}
        <div className="flex justify-end">
          <Button onClick={onDone}>{doneLabel}</Button>
        </div>
      </Card>
    </div>
  );
}

export default SigningLinkResult;
